/**
 * The client's US tax form (W-8BEN or W-9) and their "U.S. documents" (S-31).
 *
 * Every line of the form is pre-filled by the server from the identity file; the screen shows it
 * read-only and adds nothing the file already holds. The words of the declaration come from the
 * server too (the IRS's own, sealed with a version): what is shown is what is signed.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export type TaxFormKind = 'w8ben' | 'w9';

export type TaxFormState =
  | 'not_required'
  | 'not_signed'
  | 'valid'
  | 'renewal_due'
  | 'expired'
  | 'invalidated';

export interface TaxFormItem {
  key: 'tax_form';
  form: TaxFormKind;
  state: TaxFormState;
  signed_on: string | null;
  valid_through: string | null;
  renewal_opens_on: string | null;
  reasons: string[];
  signature_id: string | null;
  action: 'sign' | null;
}

export interface SimpleDocumentItem {
  key: 'accreditation' | 'electronic_delivery' | 'offering_package';
  state: 'on_file';
  action: 'withdraw' | null;
  category?: string;
  expires_at?: string | null;
  issuers?: number;
  latest?: string;
}

export type UsDocumentItem = TaxFormItem | SimpleDocumentItem;

export interface UsDocuments {
  applies: boolean;
  items: UsDocumentItem[];
}

export interface CertificationText {
  version: string;
  heading: string;
  intro: string;
  items: string[];
  closing: string;
  capacity: string | null;
}

/** Every printed line, as the server fills it (W-8BEN lines 1-10, or the W-9's). */
export type PrintedLines = Record<string, string | boolean>;

export interface CarriedEntries {
  us_tin: string;
  mailing_street: string;
  mailing_city: string;
  mailing_country: string;
}

export interface TaxFormPreview {
  applies: boolean;
  form?: TaxFormKind;
  revision?: string;
  lines?: PrintedLines;
  /** Hash of the lines and the declaration shown: sent back with the signature. */
  lines_sha256?: string;
  /** What the person typed on their previous form and the profile does not hold: pre-filled. */
  carried?: CarriedEntries;
  needs_us_tin?: boolean;
  us_tin_required?: boolean;
  missing?: string[];
  contradicts?: string[];
  certification?: CertificationText;
  esign_template?: string;
  step_up_required?: boolean;
  legal_name?: string;
  /** What to do when the file of a Mexican resident carries a generic or no RFC; null when it does not. */
  rfc_message?: string | null;
}

export interface SignTaxFormInput {
  signerName: string;
  language: 'en' | 'es';
  productId: string | null;
  /** null: not sent, the server carries forward the previous form's. */
  usTin: string | null;
  mailingStreet: string;
  mailingCity: string;
  mailingCountry: string;
  /** W-9: the IRS has notified the person that they are subject to backup withholding. */
  backupWithholdingNotified: boolean;
  mfaCode: string | null;
  certificationVersion: string;
  linesSha256: string;
}

export interface SignedTaxForm {
  signature_id: string;
  form: TaxFormKind;
  signed_on: string;
  valid_through: string | null;
}

export interface HardCopy {
  url: string;
  sha256: string;
}

class TaxFormsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async usDocuments(): Promise<UsDocuments> {
    const res = await this.api.get<UsDocuments>('/api/v1/tax-forms/us-documents');
    return res.data;
  }

  async current(productId: string | null): Promise<TaxFormPreview> {
    const res = await this.api.get<TaxFormPreview>('/api/v1/tax-forms/current', {
      params: productId ? { product_id: productId } : undefined,
    });
    return res.data;
  }

  /** The key makes a retry of the same signing the same signing (a double click, a dropped
   *  connection): the server returns the first answer instead of signing twice. */
  async sign(input: SignTaxFormInput, idempotencyKey: string): Promise<SignedTaxForm> {
    const res = await this.api.post<SignedTaxForm>(
      '/api/v1/tax-forms/sign',
      {
        signer_name: input.signerName,
        language: input.language,
        product_id: input.productId,
        us_tin: input.usTin,
        mailing_street: input.mailingStreet,
        mailing_city: input.mailingCity,
        mailing_country: input.mailingCountry,
        backup_withholding_notified: input.backupWithholdingNotified,
        mfa_code: input.mfaCode,
        certification_version: input.certificationVersion,
        lines_sha256: input.linesSha256,
        confirms_certification: true,
      },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
    return res.data;
  }

  async hardCopy(signatureId: string): Promise<HardCopy> {
    const res = await this.api.get<HardCopy>(`/api/v1/tax-forms/${signatureId}/hard-copy`);
    return res.data;
  }
}

export default new TaxFormsService();
